import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { ticketService } from '../../services/api';
import { toast } from 'sonner';
import { 
  Loader2, 
  CheckCircle, 
  AlertTriangle, 
  Users, 
  Package,
  ChevronDown,
  ChevronRight,
  Check,
  X,
  Zap,
  MapPin,
  Crosshair
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { findStadium } from '../../data/stadiums';
import { getMatchByNumber } from '../../data/worldCupSchedule';
import FloatingPlanWindow from './FloatingPlanWindow';

// Format event name: 002 → M2, M002 → M2
const formatEventName = (eventName) => {
  if (!eventName) return '-';
  return eventName.replace(/\b[M]?0*(\d{1,3})\b/gi, (match, num) => `M${parseInt(num, 10)}`);
};

export const AttribuerVenteModal = ({ 
  open, 
  onOpenChange, 
  venteTicket, 
  project = 'WORLD CUP 2026',
  onSuccess,
  onSpecialAttribution,
  onShowStadiumPlan
}) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [validateOnSubmit, setValidateOnSubmit] = useState(false);
  const [floatingPlan, setFloatingPlan] = useState(null); // { stadium, matchInfo }
  // Placement-based search (Block + Rang) state
  const [placementMode, setPlacementMode] = useState(false);
  const [placementData, setPlacementData] = useState(null);
  const [loadingPlacement, setLoadingPlacement] = useState(false);

  // Fetch available ACHATs when modal opens
  useEffect(() => {
    if (open && venteTicket?.id) {
      fetchAvailableAchats();
    } else {
      // Reset state when modal closes
      setData(null);
      setSelectedIds(new Set());
      setExpandedGroups(new Set());
      setValidateOnSubmit(false);
      setPlacementMode(false);
      setPlacementData(null);
    }
  }, [open, venteTicket?.id]);

  const fetchAvailableAchats = async () => {
    setLoading(true);
    try {
      const result = await ticketService.getAvailableAchats(venteTicket.id, project);
      setData(result);
      
      // For "Compléter" flow: target the REMAINING qty, not the full vente_qty
      const isPartial = result.attribution_status === 'partial';
      const baseQty = result.vente_qty || venteTicket?.qty || 1;
      const qty = isPartial ? Math.max(0, baseQty - (result.partial_qty || 0)) : baseQty;
      
      // Auto-expand the OPTIMAL group (exact match to qty), or first group with enough tickets
      if (result.groups?.length > 0) {
        // Find optimal group (exact match)
        const optimalGroup = result.groups.find(g => g.count === qty);
        // Or find first group with enough tickets
        const sufficientGroup = result.groups.find(g => g.count >= qty);
        
        const groupToExpand = optimalGroup || sufficientGroup || result.groups[0];
        setExpandedGroups(new Set([groupToExpand.email_compte]));
      }
    } catch (error) {
      console.error('Error fetching available achats:', error);
      toast.error('Erreur lors du chargement des ACHAT disponibles');
    } finally {
      setLoading(false);
    }
  };

  const fetchByPlacement = async () => {
    setLoadingPlacement(true);
    setSelectedIds(new Set());
    try {
      const result = await ticketService.getAchatsByPlacement(venteTicket.id, project);
      setPlacementData(result);
      setPlacementMode(true);
      // Auto-expand the first group (most likely match)
      if (result.groups?.length > 0) {
        setExpandedGroups(new Set([result.groups[0].email_compte]));
        // Auto-select all tickets if exact qty match (use remaining qty if partial)
        const isPartial = data?.attribution_status === 'partial';
        const baseQty = result.vente_qty || 1;
        const targetQty = isPartial ? Math.max(0, baseQty - (data?.partial_qty || 0)) : baseQty;
        const optimalGroup = result.groups.find(g => g.count === targetQty);
        if (optimalGroup) {
          setSelectedIds(new Set(optimalGroup.tickets.map(t => t.id)));
        }
      }
      if (result.total_available === 0) {
        toast.warning(`Aucun ACHAT trouvé au Block ${result.vente_block} · Rang ${result.vente_rang}`);
      } else {
        toast.success(`${result.total_available} ACHAT trouvé(s) au Block ${result.vente_block} · Rang ${result.vente_rang}`);
      }
    } catch (error) {
      console.error('Error fetching achats by placement:', error);
      toast.error('Erreur lors de la recherche par Block/Rang');
    } finally {
      setLoadingPlacement(false);
    }
  };

  const exitPlacementMode = () => {
    setPlacementMode(false);
    setPlacementData(null);
    setSelectedIds(new Set());
    setExpandedGroups(new Set());
  };

  const toggleGroup = (email) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(email)) {
        next.delete(email);
      } else {
        next.add(email);
      }
      return next;
    });
  };

  const toggleTicket = (ticketId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(ticketId)) {
        next.delete(ticketId);
      } else {
        next.add(ticketId);
      }
      return next;
    });
  };

  const selectGroupTickets = (tickets, count) => {
    const ticketIds = tickets.slice(0, count).map(t => t.id);
    setSelectedIds(prev => {
      const next = new Set(prev);
      // Remove all from this group first
      tickets.forEach(t => next.delete(t.id));
      // Add the selected count
      ticketIds.forEach(id => next.add(id));
      return next;
    });
  };

  const selectAllFromGroup = (tickets) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      tickets.forEach(t => next.add(t.id));
      return next;
    });
  };

  const deselectAllFromGroup = (tickets) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      tickets.forEach(t => next.delete(t.id));
      return next;
    });
  };

  const handleSubmit = async () => {
    if (selectedIds.size === 0) {
      toast.error('Sélectionnez au moins un ticket ACHAT');
      return;
    }

    // Block if not exact match
    if (selectedIds.size !== requiredQty) {
      if (selectedIds.size < requiredQty) {
        toast.error(`Il manque ${requiredQty - selectedIds.size} billet(s) à attribuer. Sélectionnez exactement ${requiredQty} billet(s).`);
      } else {
        toast.error(`Trop de billets sélectionnés. Sélectionnez exactement ${requiredQty} billet(s).`);
      }
      return;
    }

    setSubmitting(true);
    try {
      let result;
      if (isPartialMode) {
        // « Compléter » : on appelle partial-attribution. Le backend bascule en 'full'
        // automatiquement quand total_linked >= vente_qty.
        result = await ticketService.partialAttribution(
          venteTicket.id,
          Array.from(selectedIds),
          project
        );
        toast.success(result.message || `VENTE complétée (${venteQty}/${venteQty})`);
      } else {
        result = await ticketService.manualAttribution(
          venteTicket.id,
          Array.from(selectedIds),
          validateOnSubmit,
          project
        );
        toast.success(result.message);
      }
      
      // Rafraîchir les données AVANT de fermer le modal
      if (onSuccess) {
        await onSuccess();
      }
      
      onOpenChange(false);
    } catch (error) {
      console.error('Error performing attribution:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'attribution');
    } finally {
      setSubmitting(false);
    }
  };

  // Attribution PARTIELLE : lie moins d'ACHAT que la qty de la VENTE
  const handlePartialSubmit = async () => {
    if (selectedIds.size === 0) {
      toast.error('Sélectionnez au moins un ticket ACHAT à pré-attribuer');
      return;
    }
    if (selectedIds.size >= requiredQty) {
      toast.error("Sélection complète : utilisez le bouton « Attribuer » classique.");
      return;
    }
    setSubmitting(true);
    try {
      const result = await ticketService.partialAttribution(
        venteTicket.id,
        Array.from(selectedIds),
        project
      );
      toast.success(result.message || `Pré-attribution: ${selectedIds.size}/${requiredQty}`);
      if (onSuccess) await onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error('Error partial attribution:', error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'attribution partielle");
    } finally {
      setSubmitting(false);
    }
  };

  // Check if selection matches VENTE qty
  const venteQtyTotal = data?.vente_qty || venteTicket?.qty || 1;
  // Partial attribution context (for "Compléter" flow)
  const isPartialMode = (data?.attribution_status === 'partial') || (venteTicket?.attribution_status === 'partial');
  const partialQty = isPartialMode ? (data?.partial_qty || venteTicket?.partial_qty || 0) : 0;
  const remainingQty = Math.max(0, venteQtyTotal - partialQty);
  const alreadyLinkedAchats = data?.already_linked_achats || [];
  // `venteQty` is the quantity to SELECT in this modal step :
  //   - normal mode  → vente qty
  //   - partial/complete mode → remaining qty (vente_qty - partial_qty)
  // This way all the existing algorithms (adjacency, sort, badges...) keep working.
  const venteQty = isPartialMode ? remainingQty : venteQtyTotal;
  const requiredQty = venteQty;
  const isExactMatch = selectedIds.size === requiredQty;
  const isTooMany = selectedIds.size > requiredQty;
  const isTooFew = selectedIds.size > 0 && selectedIds.size < requiredQty;
  const isInvalid = isTooMany || isTooFew;

  // Detect cross-account adjacent seat combinations
  const crossAccountSuggestion = useMemo(() => {
    if (!data?.groups || venteQty <= 1) return null;
    // Check if any single group has enough
    const singleGroupEnough = data.groups.some(g => g.count >= venteQty);
    if (singleGroupEnough) return null;

    // Collect ALL tickets with seat info across all groups
    const allTickets = [];
    for (const group of data.groups) {
      for (const t of (group.tickets || [])) {
        const place = parseInt(t.place, 10);
        const rang = (t.rang || '').trim();
        const block = (t.block || t.acces || '').trim();
        if (!isNaN(place) && rang) {
          allTickets.push({ id: t.id, place, rang, block, email: group.email_compte, groupName: group.email_compte });
        }
      }
    }

    if (allTickets.length < venteQty) return null;

    // Group by block+rang (same row)
    const byRow = {};
    for (const t of allTickets) {
      const key = `${t.block}|${t.rang}`;
      if (!byRow[key]) byRow[key] = [];
      byRow[key].push(t);
    }

    // For each row, find consecutive sequences of length >= venteQty
    for (const [rowKey, tickets] of Object.entries(byRow)) {
      if (tickets.length < venteQty) continue;
      tickets.sort((a, b) => a.place - b.place);

      // Find all consecutive runs
      for (let start = 0; start <= tickets.length - venteQty; start++) {
        const combo = [tickets[start]];
        for (let j = start + 1; j < tickets.length && combo.length < venteQty; j++) {
          if (tickets[j].place === combo[combo.length - 1].place + 1) {
            combo.push(tickets[j]);
          } else {
            break;
          }
        }

        if (combo.length >= venteQty) {
          const accounts = [...new Set(combo.map(t => t.email))];
          if (accounts.length > 1) {
            // Multi-account combo found!
            const [blk, rng] = rowKey.split('|');
            const seats = combo.map(t => t.place);
            const details = accounts.map(email => {
              const acctSeats = combo.filter(t => t.email === email).map(t => t.place);
              return { email, seats: acctSeats };
            });
            return {
              seats,
              rang: rng,
              block: blk,
              accounts: details,
              ticketIds: combo.slice(0, venteQty).map(t => t.id),
            };
          }
        }
      }
    }
    return null;
  }, [data, venteQty]);

  const applySuggestion = () => {
    if (!crossAccountSuggestion) return;
    setSelectedIds(new Set(crossAccountSuggestion.ticketIds));
    toast.success(`${crossAccountSuggestion.ticketIds.length} places côte à côte sélectionnées !`);
  };

  // Detect if current selection has non-adjacent seats
  const selectionWarning = useMemo(() => {
    if (selectedIds.size < 2) return null;
    
    // Get all selected tickets with seat info
    const allTickets = (data?.groups || []).flatMap(g => g.tickets || []);
    const selected = allTickets.filter(t => selectedIds.has(t.id));
    
    const withSeats = selected.map(t => ({
      id: t.id,
      rang: (t.rang || '').trim(),
      place: parseInt(t.place, 10),
      block: (t.block || t.acces || '').trim(),
    })).filter(t => !isNaN(t.place) && t.rang);

    if (withSeats.length < 2) return null;

    // Check if all same block+rang and consecutive
    const blocks = [...new Set(withSeats.map(t => t.block))];
    const rangs = [...new Set(withSeats.map(t => t.rang))];
    
    if (blocks.length > 1 || rangs.length > 1) {
      return { type: 'not_adjacent', message: 'Les places sélectionnées ne sont pas sur le même rang !' };
    }

    const seats = withSeats.map(t => t.place).sort((a, b) => a - b);
    const isConsecutive = seats.every((s, i) => i === 0 || s === seats[i - 1] + 1);
    
    if (!isConsecutive) {
      return { type: 'not_adjacent', message: `Places non consécutives ! (${seats.join(', ')})` };
    }

    return null;
  }, [selectedIds, data]);

  // Find all adjacent combos available (for suggestions)
  const adjacentCombos = useMemo(() => {
    if (!data?.groups || venteQty < 2) return [];
    
    const allTickets = (data.groups || []).flatMap(g =>
      (g.tickets || []).map(t => ({
        ...t,
        email: g.email_compte,
      }))
    );

    // Group by block+rang
    const byRow = {};
    for (const t of allTickets) {
      const place = parseInt(t.place, 10);
      const rang = (t.rang || '').trim();
      const block = (t.block || t.acces || '').trim();
      if (isNaN(place) || !rang) continue;
      const key = `${block}|${rang}`;
      if (!byRow[key]) byRow[key] = [];
      byRow[key].push({ ...t, placeNum: place, blockKey: block, rangKey: rang });
    }

    const combos = [];
    for (const [, tickets] of Object.entries(byRow)) {
      if (tickets.length < venteQty) continue;
      tickets.sort((a, b) => a.placeNum - b.placeNum);

      for (let start = 0; start <= tickets.length - venteQty; start++) {
        const combo = [tickets[start]];
        for (let j = start + 1; j < tickets.length && combo.length < venteQty; j++) {
          if (tickets[j].placeNum === combo[combo.length - 1].placeNum + 1) {
            combo.push(tickets[j]);
          } else break;
        }
        if (combo.length >= venteQty) {
          const accounts = [...new Set(combo.map(t => t.email))];
          combos.push({
            block: combo[0].blockKey,
            rang: combo[0].rangKey,
            seats: combo.map(t => t.placeNum),
            ids: combo.slice(0, venteQty).map(t => t.id),
            accounts,
            multiAccount: accounts.length > 1,
          });
        }
      }
    }
    return combos;
  }, [data, venteQty]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col bg-zinc-900 border-[#C4A052]/30"
        onPointerDownOutside={(e) => {
          // Don't close if clicking on floating plan window
          if (e.target?.closest('[data-testid="floating-plan-window"]')) {
            e.preventDefault();
          }
        }}
        onInteractOutside={(e) => {
          if (e.target?.closest('[data-testid="floating-plan-window"]')) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-xl text-[#C4A052] flex items-center gap-2">
            <Package className="h-5 w-5" />
            {isPartialMode ? 'Compléter l\'attribution partielle' : 'Attribution manuelle'}
          </DialogTitle>
        </DialogHeader>

        {/* VENTE Info */}
        {venteTicket && (
          <div className="bg-zinc-800 rounded-lg p-4 border border-zinc-700">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded font-bold">VENTE</span>
                  <span className="text-white font-mono text-sm">{venteTicket.ref_vente}</span>
                </div>
                <p className="text-zinc-300">{formatEventName(venteTicket.evenement)}</p>
                <p className="text-sm text-zinc-400 mt-1">
                  Catégorie: <span className="text-[#C4A052]">{venteTicket.categorie || 'N/A'}</span>
                  {' '} · {' '}
                  Client: <span className="text-zinc-300">{venteTicket.nom_client || 'N/A'}</span>
                </p>
                {/* Stadium info */}
                {(() => {
                  // Try from ticket lieu/ville first
                  let stadium = findStadium(venteTicket.lieu, venteTicket.ville, venteTicket.evenement, project);
                  let stadiumCity = '';
                  // ONLY fall back to World Cup schedule when the project is WORLD CUP
                  if (!stadium && (project || '').toUpperCase().startsWith('WORLD CUP')) {
                    const matchNumMatch = venteTicket.evenement?.match(/M(\d+)/i);
                    if (matchNumMatch) {
                      const wcMatch = getMatchByNumber(parseInt(matchNumMatch[1], 10));
                      if (wcMatch) {
                        stadium = findStadium(wcMatch.venue, wcMatch.city, venteTicket.evenement, project);
                        stadiumCity = wcMatch.city;
                      }
                    }
                  }
                  return stadium ? (
                    <div className="flex items-center gap-2 mt-2 bg-zinc-700/50 rounded px-3 py-1.5">
                      <MapPin className="h-4 w-4 text-[#C4A052]" />
                      <span className="text-sm text-white font-medium">{stadium.name}</span>
                      <span className="text-xs text-zinc-400">{stadiumCity || stadium.city}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 px-2 text-xs text-[#C4A052] hover:bg-[#C4A052]/10 ml-auto"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          const matchNumMatch2 = venteTicket.evenement?.match(/M(\d+)/i);
                          setFloatingPlan({
                            stadium,
                            matchInfo: {
                              matchNum: matchNumMatch2 ? `M${matchNumMatch2[1]}` : null,
                              eventName: venteTicket.evenement,
                              categorie: venteTicket.categorie
                            }
                          });
                        }}
                      >
                        <MapPin className="h-3 w-3 mr-1" /> Voir Plan
                      </Button>
                    </div>
                  ) : null;
                })()}
                {(venteTicket.tribune || venteTicket.acces || venteTicket.escalier || venteTicket.rang || venteTicket.place || venteTicket.block) && (
                  <div className="flex items-center gap-2 mt-2">
                    {venteTicket.tribune && <span className="text-sm text-[#C4A052] font-semibold">Tribune: {venteTicket.tribune}</span>}
                    {venteTicket.acces && <span className="text-sm text-[#C4A052] font-semibold">Accès: {venteTicket.acces}</span>}
                    {venteTicket.escalier && <span className="text-sm text-[#C4A052] font-semibold">Esc: {venteTicket.escalier}</span>}
                    {venteTicket.block && <span className="text-sm text-[#C4A052] font-semibold">Block: {venteTicket.block}</span>}
                    {venteTicket.rang && <span className="text-sm text-[#C4A052] font-semibold">Rang: {venteTicket.rang}</span>}
                    {venteTicket.place && <span className="text-sm text-[#C4A052] font-semibold">Place: {venteTicket.place}{venteTicket.place_fin ? ` - ${venteTicket.place_fin}` : ''}</span>}
                  </div>
                )}
                {venteTicket.note && (
                  <div className="mt-2 bg-amber-500/15 border border-amber-500/40 rounded-lg px-3 py-2">
                    <span className="text-base text-amber-300 font-bold">{venteTicket.note}</span>
                  </div>
                )}
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-[#C4A052]">{venteQtyTotal}</div>
                <div className="text-xs text-zinc-400">billet(s) à attribuer</div>
                {isPartialMode && (
                  <div className="mt-1 text-xs font-semibold text-sky-300 bg-sky-500/15 border border-sky-400/40 rounded px-2 py-0.5 inline-block">
                    Compléter : {partialQty}/{venteQtyTotal} attribués · {remainingQty} restant
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Already-linked ACHATs panel (partial-attribution "Compléter" flow) */}
        {isPartialMode && alreadyLinkedAchats.length > 0 && (
          <div className="bg-sky-500/10 border border-sky-400/40 rounded-lg p-3" data-testid="already-linked-achats-panel">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-sky-300" />
                <span className="text-sm font-semibold text-sky-200">
                  {alreadyLinkedAchats.length} ACHAT déjà attribué(s) à cette VENTE
                </span>
              </div>
              <span className="text-xs text-sky-300/80">
                Sélectionnez {remainingQty} ACHAT supplémentaire(s) ci-dessous pour compléter
              </span>
            </div>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {alreadyLinkedAchats.map((a) => (
                <div
                  key={a.id}
                  className="flex items-center gap-3 px-3 py-1.5 rounded bg-sky-500/15 border border-sky-400/30 text-sm"
                >
                  <Check className="h-4 w-4 text-sky-300 flex-shrink-0" />
                  <span className="text-zinc-200 font-mono text-xs">{a.ref_achat || a.id?.slice(0, 8)}</span>
                  <span className="text-zinc-400">·</span>
                  <span className="text-zinc-300 truncate max-w-[200px]">{a.email_compte || '—'}</span>
                  {(a.block || a.rang || a.place) && (
                    <span className="ml-auto text-[#C4A052] font-bold">
                      {a.block && `B:${a.block} `}{a.rang && `R:${a.rang} `}{a.place && `P:${a.place}`}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex-1 flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#C4A052]" />
            <span className="ml-3 text-zinc-400">Recherche des ACHAT compatibles...</span>
          </div>
        )}

        {/* No Results - Show special attribution option */}
        {!loading && data && data.total_available === 0 && !placementMode && (
          <div className="flex-1 flex flex-col items-center justify-center py-12">
            <AlertTriangle className="h-12 w-12 text-amber-500 mb-4" />
            <p className="text-zinc-300 text-lg">Aucun ACHAT disponible</p>
            <p className="text-zinc-500 text-sm mt-2 mb-6">
              Aucun ticket ACHAT ne correspond à cet événement et catégorie
            </p>
            
            <div className="flex flex-col items-center gap-3">
              {/* NEW: Block/Rang search button - visible when VENTE has block + rang */}
              {venteTicket?.block && venteTicket?.rang && (
                <Button
                  onClick={fetchByPlacement}
                  disabled={loadingPlacement}
                  className="bg-[#C4A052] hover:bg-[#D4B86A] text-black font-semibold"
                  data-testid="search-by-placement-btn"
                >
                  {loadingPlacement ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Crosshair className="h-4 w-4 mr-2" />
                  )}
                  Trouver par Block {venteTicket.block} · Rang {venteTicket.rang}
                </Button>
              )}
              
              {onSpecialAttribution && (
                <Button
                  onClick={() => {
                    onOpenChange(false);
                    onSpecialAttribution(venteTicket);
                  }}
                  className="bg-amber-500 hover:bg-amber-600 text-black font-semibold"
                >
                  <Zap className="h-4 w-4 mr-2" />
                  Attribution Spéciale (Autre catégorie)
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Placement-based search results */}
        {!loading && placementMode && placementData && (
          <>
            <div className="flex items-center justify-between text-sm py-2 border-b border-[#C4A052]/40 bg-[#C4A052]/10 px-3 rounded-t">
              <div className="flex items-center gap-2">
                <Crosshair className="h-4 w-4 text-[#C4A052]" />
                <span className="text-[#C4A052] font-bold">Recherche par Block {placementData.vente_block} · Rang {placementData.vente_rang}</span>
                <span className="text-zinc-400">·</span>
                <span className="text-zinc-400">
                  <span className="text-[#C4A052] font-bold">{placementData.total_available}</span> ACHAT trouvé(s)
                </span>
              </div>
              <Button size="sm" variant="ghost" onClick={exitPlacementMode} className="h-7 text-xs text-zinc-400 hover:text-white">
                <X className="h-3 w-3 mr-1" /> Retour
              </Button>
            </div>
            
            {placementData.total_available === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center py-12">
                <AlertTriangle className="h-10 w-10 text-amber-500 mb-3" />
                <p className="text-zinc-300">Aucun ACHAT au Block <strong>{placementData.vente_block}</strong> · Rang <strong>{placementData.vente_rang}</strong></p>
                <p className="text-zinc-500 text-sm mt-2">Sur le match {formatEventName(venteTicket?.evenement)}</p>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto space-y-2 pt-3 pb-2">
                {placementData.groups.map((group) => {
                  const isOpen = expandedGroups.has(group.email_compte);
                  const allSelected = group.tickets.every(t => selectedIds.has(t.id));
                  return (
                    <div key={group.email_compte} className="border border-zinc-700 rounded bg-zinc-900/50">
                      <div className="flex items-center justify-between px-3 py-2 bg-zinc-800/60 cursor-pointer hover:bg-zinc-800" onClick={() => toggleGroup(group.email_compte)}>
                        <div className="flex items-center gap-2">
                          {isOpen ? <ChevronDown className="h-4 w-4 text-zinc-400" /> : <ChevronRight className="h-4 w-4 text-zinc-400" />}
                          <Users className="h-4 w-4 text-blue-400" />
                          <span className="text-sm text-white font-medium">{group.email_compte}</span>
                          <span className="text-xs bg-[#C4A052]/20 text-[#C4A052] px-2 py-0.5 rounded">{group.category || 'Sans cat.'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-zinc-400">{group.count} billet{group.count > 1 ? 's' : ''}</span>
                          <Button size="sm" variant="ghost" className="h-6 text-xs px-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedIds(prev => {
                                const next = new Set(prev);
                                if (allSelected) group.tickets.forEach(t => next.delete(t.id));
                                else group.tickets.forEach(t => next.add(t.id));
                                return next;
                              });
                            }}>
                            {allSelected ? 'Désélect.' : 'Tout sélect.'}
                          </Button>
                        </div>
                      </div>
                      {isOpen && (
                        <div className="divide-y divide-zinc-800">
                          {group.tickets.map(t => (
                            <label key={t.id} className={cn(
                              "flex items-center justify-between gap-3 px-3 py-2 cursor-pointer hover:bg-zinc-800/40",
                              selectedIds.has(t.id) && "bg-emerald-900/20"
                            )}>
                              <div className="flex items-center gap-3">
                                <Checkbox checked={selectedIds.has(t.id)} onCheckedChange={() => toggleTicket(t.id)} />
                                <span className="text-sm text-zinc-200">
                                  <span className="text-[#C4A052] font-bold">B:{t.block}</span>
                                  <span className="text-zinc-500 mx-1">·</span>
                                  <span className="text-[#C4A052] font-bold">R:{t.rang}</span>
                                  <span className="text-zinc-500 mx-1">·</span>
                                  <span className="text-[#C4A052] font-bold">P:{t.place || '-'}</span>
                                </span>
                                <span className="text-xs text-zinc-500">{t.categorie || '-'}</span>
                              </div>
                              <span className="text-xs text-zinc-500">{t.ref_achat || ''}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Footer - same as normal mode but for placement results */}
            {placementData.total_available > 0 && (
              <div className="border-t border-zinc-700 pt-4 space-y-3">
                {/* Validate Option */}
                <label className="flex items-center gap-3 cursor-pointer">
                  <Checkbox
                    checked={validateOnSubmit}
                    onCheckedChange={setValidateOnSubmit}
                    className="data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                  />
                  <span className="text-sm text-zinc-300">
                    Marquer comme <span className="text-emerald-400 font-semibold">VALIDÉ</span> (protégé contre les modifications)
                  </span>
                </label>

                {/* Selection Status */}
                <div className={cn(
                  "flex items-center gap-2 px-3 py-2 rounded-full text-sm font-semibold w-fit",
                  isExactMatch ? "bg-emerald-500/20 text-emerald-400 ring-1 ring-emerald-500/50" :
                  isInvalid ? "bg-red-500/20 text-red-400 ring-1 ring-red-500/50" :
                  "bg-zinc-800 text-zinc-400"
                )}>
                  {isExactMatch ? <CheckCircle className="h-4 w-4" /> :
                   isInvalid ? <AlertTriangle className="h-4 w-4" /> :
                   <Package className="h-4 w-4" />}
                  <span>{selectedIds.size} / {venteQty} sélectionné(s)</span>
                </div>

                {/* Warnings */}
                {isTooMany && (
                  <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/20 p-3 rounded border border-red-500/30">
                    <AlertTriangle className="h-5 w-5 flex-shrink-0" />
                    <span><strong>BLOQUÉ:</strong> Vous avez sélectionné {selectedIds.size} billets mais la VENTE n'en demande que {venteQty}. Désélectionnez {selectedIds.size - venteQty} billet(s) pour continuer.</span>
                  </div>
                )}
                {isTooFew && (
                  <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/20 p-3 rounded border border-red-500/30">
                    <AlertTriangle className="h-5 w-5 flex-shrink-0" />
                    <span><strong>INCOMPLET:</strong> Il manque {venteQty - selectedIds.size} billet(s). Sélectionnez exactement {venteQty} billet(s) pour valider.</span>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex items-center justify-end gap-3">
                  <Button
                    variant="outline"
                    onClick={() => onOpenChange(false)}
                    disabled={submitting}
                    className="border-zinc-600 text-zinc-300"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Annuler
                  </Button>
                  {/* Bouton Attribution Partielle (bleu ciel) — visible quand sélection > 0 et < qty */}
                  {isTooFew && (
                    <Button
                      onClick={handlePartialSubmit}
                      disabled={submitting}
                      className="font-semibold bg-sky-500 hover:bg-sky-600 text-white border border-sky-400 shadow-md"
                      data-testid="placement-partial-btn"
                      title="Pré-attribue les ACHAT sélectionnés. La VENTE reste partiellement à compléter."
                    >
                      {submitting
                        ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />En cours…</>
                        : <>⚡ Attribution Partielle ({selectedIds.size}/{venteQty})</>}
                    </Button>
                  )}
                  <Button
                    onClick={handleSubmit}
                    disabled={submitting || selectedIds.size === 0 || isInvalid}
                    className={cn(
                      "font-semibold",
                      isInvalid ? "bg-red-500/50 text-red-200 cursor-not-allowed border border-red-500/50"
                        : isExactMatch ? "bg-emerald-500 hover:bg-emerald-600 text-white"
                        : "bg-zinc-600 text-zinc-400 cursor-not-allowed"
                    )}
                    data-testid="placement-attribuer-btn"
                  >
                    {submitting ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Attribution...</>
                    ) : isTooMany ? (
                      <><AlertTriangle className="h-4 w-4 mr-2" />Trop de billets ({selectedIds.size}/{venteQty})</>
                    ) : isTooFew ? (
                      <><AlertTriangle className="h-4 w-4 mr-2" />Il manque {venteQty - selectedIds.size} billet(s)</>
                    ) : isExactMatch ? (
                      <><CheckCircle className="h-4 w-4 mr-2" />Attribuer {selectedIds.size} ACHAT(s)</>
                    ) : (
                      <><Package className="h-4 w-4 mr-2" />Sélectionnez {venteQty} billet(s)</>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Results */}
        {!loading && !placementMode && data && data.total_available > 0 && (
          <>
            {/* Summary */}
            <div className="flex items-center justify-between text-sm py-2 border-b border-zinc-700">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-zinc-400">
                  <span className="text-[#C4A052] font-bold">{data.total_available}</span> ACHAT disponible(s) 
                  dans <span className="text-blue-400">{data.groups?.length}</span> compte(s)
                </span>
                {/* NEW: Block/Rang search button - visible when VENTE has block + rang */}
                {venteTicket?.block && venteTicket?.rang && (
                  <Button
                    size="sm"
                    onClick={fetchByPlacement}
                    disabled={loadingPlacement}
                    className="h-7 text-xs bg-[#C4A052]/20 hover:bg-[#C4A052]/30 text-[#C4A052] border border-[#C4A052]/40"
                    data-testid="search-by-placement-toolbar-btn"
                  >
                    {loadingPlacement ? (
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    ) : (
                      <Crosshair className="h-3 w-3 mr-1" />
                    )}
                    Trouver Block {venteTicket.block} · Rang {venteTicket.rang}
                  </Button>
                )}
              </div>
              <div className={cn(
                "flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold",
                isExactMatch ? "bg-emerald-500/20 text-emerald-400 ring-1 ring-emerald-500/50" :
                isInvalid ? "bg-red-500/20 text-red-400 ring-1 ring-red-500/50" :
                "bg-zinc-800 text-zinc-400"
              )}>
                {isExactMatch ? <CheckCircle className="h-4 w-4" /> :
                 isInvalid ? <AlertTriangle className="h-4 w-4" /> :
                 <Package className="h-4 w-4" />}
                <span>{selectedIds.size} / {venteQty} sélectionné(s)</span>
              </div>
            </div>

            {/* Cross-account suggestion */}
            {crossAccountSuggestion && (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 space-y-2" data-testid="cross-account-suggestion">
                <div className="flex items-start gap-2">
                  <Zap className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-amber-300 font-medium">
                      {venteQty} places côte à côte trouvées sur {crossAccountSuggestion.accounts.length} comptes !
                    </p>
                    <p className="text-xs text-zinc-400 mt-1">
                      {crossAccountSuggestion.block && <>Bloc {crossAccountSuggestion.block} · </>}
                      Rang {crossAccountSuggestion.rang} · Places {crossAccountSuggestion.seats[0]}-{crossAccountSuggestion.seats[crossAccountSuggestion.seats.length - 1]}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-1.5">
                      {crossAccountSuggestion.accounts.map((a, i) => (
                        <span key={i} className="text-xs bg-zinc-800 text-zinc-300 px-2 py-0.5 rounded">
                          {a.email.length > 25 ? a.email.slice(0, 22) + '...' : a.email}: P:{a.seats.join(',')}
                        </span>
                      ))}
                    </div>
                  </div>
                  <Button size="sm" onClick={applySuggestion} className="bg-amber-500 hover:bg-amber-600 text-black text-xs font-semibold flex-shrink-0" data-testid="apply-suggestion-btn">
                    Sélectionner
                  </Button>
                </div>
              </div>
            )}

            {/* Warning: non-adjacent selection */}
            {selectionWarning && isExactMatch && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3" data-testid="adjacency-warning">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-red-300 font-medium">{selectionWarning.message}</p>
                    {adjacentCombos.length > 0 && (
                      <div className="mt-2 space-y-1.5">
                        <p className="text-xs text-zinc-400">Places côte à côte disponibles :</p>
                        {adjacentCombos.slice(0, 3).map((combo, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <span className="text-xs text-[#C4A052]">
                              {combo.block && <>B:{combo.block} </>}R:{combo.rang} P:{combo.seats[0]}-{combo.seats[combo.seats.length - 1]}
                              {combo.multiAccount && <span className="text-amber-400 ml-1">(multi-comptes)</span>}
                            </span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => { setSelectedIds(new Set(combo.ids)); toast.success('Places côte à côte sélectionnées'); }}
                              className="h-5 px-2 text-xs text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                            >
                              Choisir
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Groups List - Sorted: exact match first, then by count descending */}
            <div className="flex-1 overflow-y-auto min-h-0 space-y-2 py-2">
              {/* Sort groups: exact match first, then closest to venteQty, then by count */}
              {[...(data.groups || [])].sort((a, b) => {
                const aExact = a.count === venteQty;
                const bExact = b.count === venteQty;
                const aClose = a.count >= venteQty;
                const bClose = b.count >= venteQty;
                
                // Exact matches first
                if (aExact && !bExact) return -1;
                if (bExact && !aExact) return 1;
                
                // Then groups with enough tickets
                if (aClose && !bClose) return -1;
                if (bClose && !aClose) return 1;
                
                // Then by closeness to venteQty
                const aDiff = Math.abs(a.count - venteQty);
                const bDiff = Math.abs(b.count - venteQty);
                return aDiff - bDiff;
              }).map((group, index) => {
                const isExpanded = expandedGroups.has(group.email_compte);
                const selectedInGroup = group.tickets.filter(t => selectedIds.has(t.id)).length;
                const isExactMatchGroup = group.count === venteQty;
                const hasEnough = group.count >= venteQty;
                const isNotOptimal = !isExactMatchGroup && !hasEnough;
                
                // Check if any group is OPTIMAL (exact match)
                const allGroups = data.groups || [];
                const hasOptimalGroup = allGroups.some(g => g.count === venteQty);
                // First group with enough tickets is "MEILLEUR" if no OPTIMAL exists
                const isBestChoice = !hasOptimalGroup && hasEnough && index === 0;
                
                // Adjacency info from backend
                const adjacency = group.adjacency || {};
                const isAdjacent = adjacency.is_adjacent === true;
                const isSameBlock = adjacency.is_same_block === true;
                
                return (
                  <div 
                    key={group.email_compte} 
                    className={cn(
                      "border rounded-lg overflow-hidden",
                      isExactMatchGroup 
                        ? "border-emerald-500/50 ring-2 ring-emerald-500/30" 
                        : isBestChoice
                          ? "border-emerald-500/30 ring-1 ring-emerald-500/20"
                          : hasEnough 
                            ? "border-blue-500/30"
                            : "border-red-500/30"  // NOT OPTIMAL = RED
                    )}
                  >
                    {/* Group Header */}
                    <button
                      onClick={() => toggleGroup(group.email_compte)}
                      className={cn(
                        "w-full flex items-center justify-between px-4 py-3 transition-colors",
                        isExactMatchGroup 
                          ? "bg-emerald-500/10 hover:bg-emerald-500/20" 
                          : isBestChoice
                            ? "bg-emerald-500/5 hover:bg-emerald-500/10"
                            : hasEnough
                              ? "bg-zinc-800 hover:bg-zinc-700"
                              : "bg-red-500/5 hover:bg-red-500/10"  // NOT OPTIMAL = RED BG
                      )}
                    >
                      <div className="flex items-center gap-3">
                        {isExpanded ? 
                          <ChevronDown className="h-4 w-4 text-zinc-400" /> : 
                          <ChevronRight className="h-4 w-4 text-zinc-400" />
                        }
                        <Users className={cn(
                          "h-4 w-4", 
                          isExactMatchGroup || isBestChoice ? "text-emerald-400" : 
                          hasEnough ? "text-blue-400" : 
                          "text-red-400"
                        )} />
                        <span className={cn(
                          "font-medium truncate max-w-[200px]",
                          isNotOptimal ? "text-red-300" : "text-white"
                        )}>
                          {group.email_compte}
                        </span>
                        {/* Show category from first ticket in group - visible without expanding */}
                        {group.tickets?.[0]?.categorie && (
                          <span className="text-xs text-[#C4A052] bg-[#C4A052]/10 px-2 py-0.5 rounded border border-[#C4A052]/30">
                            {group.tickets[0].categorie}
                          </span>
                        )}
                        {/* Badges: OPTIMAL, MEILLEUR, EMPLACEMENT, ADJACENTS, PAS ASSEZ */}
                        {group.seat_match && (
                          <span className="bg-[#C4A052] text-black text-[10px] font-bold px-1.5 py-0.5 rounded">
                            EMPLACEMENT ✓
                          </span>
                        )}
                        {isExactMatchGroup && (
                          <span className="bg-emerald-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                            OPTIMAL ✓
                          </span>
                        )}
                        {isBestChoice && (
                          <span className="bg-emerald-600/80 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                            MEILLEUR ✓
                          </span>
                        )}
                        {isAdjacent && (
                          <span className="bg-purple-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                            ADJACENTS ✓
                          </span>
                        )}
                        {!isAdjacent && isSameBlock && hasEnough && !isExactMatchGroup && !isBestChoice && (
                          <span className="bg-blue-500/80 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                            MÊME BLOC
                          </span>
                        )}
                        {isNotOptimal && (
                          <span className="bg-red-500/80 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                            PAS ASSEZ
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        {selectedInGroup > 0 && (
                          <span className="bg-emerald-500/20 text-emerald-400 text-xs px-2 py-0.5 rounded">
                            {selectedInGroup} sélectionné(s)
                          </span>
                        )}
                        <span className={cn(
                          "text-sm",
                          isNotOptimal ? "text-red-400" : "text-zinc-400"
                        )}>
                          {group.count} billet(s)
                        </span>
                      </div>
                    </button>

                    {/* Expanded Content */}
                    {isExpanded && (
                      <div className="bg-zinc-900/50 p-3 space-y-2">
                        {/* Quick Actions */}
                        <div className="flex items-center gap-2 pb-2 border-b border-zinc-800">
                          <span className="text-xs text-zinc-500">Sélection rapide:</span>
                          {group.count >= venteQty && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => { e.stopPropagation(); selectGroupTickets(group.tickets, venteQty); }}
                              className="h-6 text-xs bg-[#C4A052]/10 border-[#C4A052]/30 text-[#C4A052] hover:bg-[#C4A052]/20"
                            >
                              Sélectionner {venteQty}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); selectAllFromGroup(group.tickets); }}
                            className="h-6 text-xs"
                          >
                            Tout
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); deselectAllFromGroup(group.tickets); }}
                            className="h-6 text-xs"
                          >
                            Aucun
                          </Button>
                        </div>

                        {/* Tickets - sorted by accès/escalier/rang/place */}
                        <div className="space-y-1">
                          {[...group.tickets].sort((a, b) => {
                            const aBlock = (a.block || '').toString();
                            const bBlock = (b.block || '').toString();
                            if (aBlock !== bBlock) return aBlock.localeCompare(bBlock, undefined, { numeric: true });
                            const aAcces = (a.acces || '').toString();
                            const bAcces = (b.acces || '').toString();
                            if (aAcces !== bAcces) return aAcces.localeCompare(bAcces, undefined, { numeric: true });
                            const aEsc = (a.escalier || '').toString();
                            const bEsc = (b.escalier || '').toString();
                            if (aEsc !== bEsc) return aEsc.localeCompare(bEsc, undefined, { numeric: true });
                            const aRang = parseInt(a.rang, 10) || 99999;
                            const bRang = parseInt(b.rang, 10) || 99999;
                            if (aRang !== bRang) return aRang - bRang;
                            const aPlace = parseInt(a.place, 10) || 99999;
                            const bPlace = parseInt(b.place, 10) || 99999;
                            return aPlace - bPlace;
                          }).map((ticket) => {
                            const isSelected = selectedIds.has(ticket.id);
                            return (
                              <div
                                key={ticket.id}
                                onClick={() => toggleTicket(ticket.id)}
                                className={cn(
                                  "flex items-center gap-3 px-3 py-2 rounded cursor-pointer transition-colors",
                                  isSelected 
                                    ? "bg-emerald-500/20 border border-emerald-500/30" 
                                    : "bg-zinc-800/50 hover:bg-zinc-800 border border-transparent"
                                )}
                              >
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => toggleTicket(ticket.id)}
                                  className="data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-xs text-zinc-400">
                                      {formatEventName(ticket.evenement)}
                                    </span>
                                    {(ticket.block || ticket.rang || ticket.place) && (
                                      <span className="text-base text-[#C4A052] font-bold">
                                        {ticket.block && `B:${ticket.block} `}
                                        {ticket.rang && `R:${ticket.rang} `}
                                        {ticket.place && `P:${ticket.place}${ticket.place_fin ? `-${ticket.place_fin}` : ''}`}
                                      </span>
                                    )}
                                  </div>
                                  {(ticket.tribune || ticket.acces || ticket.escalier) && (
                                    <div className="flex items-center gap-2 mt-0.5">
                                      {ticket.tribune && <span className="text-sm text-[#C4A052] font-semibold">Tribune: {ticket.tribune}</span>}
                                      {ticket.acces && <span className="text-sm text-[#C4A052] font-semibold">Accès: {ticket.acces}</span>}
                                      {ticket.escalier && <span className="text-sm text-[#C4A052] font-semibold">Esc: {ticket.escalier}</span>}
                                    </div>
                                  )}
                                </div>
                                {isSelected && (
                                  <Check className="h-4 w-4 text-emerald-400" />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="border-t border-zinc-700 pt-4 space-y-3">
              {/* Validate Option */}
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={validateOnSubmit}
                  onCheckedChange={setValidateOnSubmit}
                  className="data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                />
                <span className="text-sm text-zinc-300">
                  Marquer comme <span className="text-emerald-400 font-semibold">VALIDÉ</span> (protégé contre les modifications)
                </span>
              </label>

              {/* Warning if too many selected - BLOCKS submission */}
              {isTooMany && (
                <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/20 p-3 rounded border border-red-500/30">
                  <AlertTriangle className="h-5 w-5 flex-shrink-0" />
                  <span>
                    <strong>BLOQUÉ:</strong> Vous avez sélectionné {selectedIds.size} billets mais la VENTE n'en demande que {venteQty}. 
                    Désélectionnez {selectedIds.size - venteQty} billet(s) pour continuer.
                  </span>
                </div>
              )}

              {/* Warning if not enough selected - BLOCKS submission */}
              {isTooFew && (
                <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/20 p-3 rounded border border-red-500/30">
                  <AlertTriangle className="h-5 w-5 flex-shrink-0" />
                  <span>
                    <strong>INCOMPLET:</strong> Il manque {venteQty - selectedIds.size} billet(s). 
                    Sélectionnez exactement {venteQty} billet(s) pour valider.
                  </span>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center justify-between gap-3">
                {/* Special Attribution Button - ALWAYS visible when callback exists */}
                {onSpecialAttribution && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      onOpenChange(false);
                      onSpecialAttribution(venteTicket);
                    }}
                    className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                  >
                    <Zap className="h-4 w-4 mr-2" />
                    Attribution Spéciale
                  </Button>
                )}
                
                <div className="flex items-center gap-3 ml-auto">
                  <Button
                    variant="outline"
                    onClick={() => onOpenChange(false)}
                    disabled={submitting}
                    className="border-zinc-600 text-zinc-300"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Annuler
                  </Button>
                  {/* Bouton Attribution Partielle (bleu ciel) */}
                  {isTooFew && (
                    <Button
                      onClick={handlePartialSubmit}
                      disabled={submitting}
                      className="font-semibold bg-sky-500 hover:bg-sky-600 text-white border border-sky-400 shadow-md"
                      title="Pré-attribue les ACHAT sélectionnés. La VENTE reste partiellement à compléter."
                    >
                      {submitting
                        ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />En cours…</>
                        : <>⚡ Attribution Partielle ({selectedIds.size}/{venteQty})</>}
                    </Button>
                  )}
                  <Button
                    onClick={handleSubmit}
                    disabled={submitting || selectedIds.size === 0 || isInvalid}
                    className={cn(
                      "font-semibold",
                      isInvalid 
                        ? "bg-red-500/50 text-red-200 cursor-not-allowed border border-red-500/50"
                        : isExactMatch 
                          ? (isPartialMode
                              ? "bg-sky-500 hover:bg-sky-600 text-white border border-sky-400"
                              : "bg-emerald-500 hover:bg-emerald-600 text-white")
                          : "bg-zinc-600 text-zinc-400 cursor-not-allowed"
                    )}
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Attribution...
                      </>
                    ) : isTooMany ? (
                      <>
                        <AlertTriangle className="h-4 w-4 mr-2" />
                        Trop de billets ({selectedIds.size}/{requiredQty})
                      </>
                    ) : isTooFew ? (
                      <>
                        <AlertTriangle className="h-4 w-4 mr-2" />
                        Il manque {requiredQty - selectedIds.size} billet(s)
                      </>
                    ) : isExactMatch ? (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        {isPartialMode
                          ? `Compléter (${venteQtyTotal}/${venteQtyTotal})`
                          : `Attribuer ${selectedIds.size} ACHAT(s)`}
                      </>
                    ) : (
                      <>
                        <Package className="h-4 w-4 mr-2" />
                        Sélectionnez {requiredQty} billet(s)
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </DialogContent>
      {floatingPlan && (
        <FloatingPlanWindow
          stadium={floatingPlan.stadium}
          matchInfo={floatingPlan.matchInfo}
          onClose={() => setFloatingPlan(null)}
        />
      )}
    </Dialog>
  );
};
